// skills/mining/miningUI.js
// Mining UI rendering and interaction

import { miningSkill } from './mining.js';
import { ROCKS, PICKAXES } from './miningData.js';
import { gameState } from '../../game/gameState.js';
import { SKILLS } from '../../game/constants.js';
import logger from '../../utils/logger.js';
import { updateSkillHeader } from '../../ui/skillHeader.js';
import { XP_TABLE, GAME_CONFIG } from '../../game/constants.js';
import { formatWithCommas, formatNumber } from '../../utils/format.js';
import { getXPPerHour } from '../../systems/experience.js';
import { IS_DEV } from '../../config/env.js';
import { adminPanel } from '../admin/admin.js';
import { getCurrentSkillId } from '../../core/routeSetup.js';
import { getItemName, getItemIcon } from '../../data/items/index.js';
import { formatPercent } from '../../utils/format.js';
import { getCorruptionSpeedMultiplier, getCorruptionLootMultiplier, getCorruptionXPMultiplier } from '../../systems/corruption.js';
import { updateToolUpgradeCost } from '../../ui/uiHelpers.js';
import { toastWarning } from '../../ui/toast.js';
import { renderSkillInfo } from '../../ui/skillInfo.js';
import { renderTalentButton, isTalentTreeOpen, getActiveSkillId } from '../../systems/talentTree/talentTreeUI.js';
import { getTalentXPBonus, getTalentLootBonus } from '../../systems/talentTree/talentTree.js';
import { getSkillIcon } from '../../core/skillRegistry.js';
import { getAtlasPercentBg } from '../../data/atlasManifest.js';

// Track loot multiplier to detect corruption changes
let lastLootMultiplier = 1;

/**
 * Render compact skill header with integrated tool upgrade
 */
function renderCompactHeader() {
    const skill = gameState.getSkill(SKILLS.MINING);
    const xpPerHour = getXPPerHour(SKILLS.MINING);

    // Calculate display values
    const isMax = skill.level >= GAME_CONFIG.MAX_LEVEL;
    const virtualEnabled = gameState.settings.virtualLevelsEnabled;
    const isVirtualMax = skill.virtualLevel >= GAME_CONFIG.VIRTUAL_LEVEL_CAP;

    let levelDisplay, progressPercent, progressText;

    if (isMax && virtualEnabled) {
        levelDisplay = `${skill.level} (${skill.virtualLevel})`;
        progressPercent = isVirtualMax ? 100 : (skill.currentXp / skill.nextLevelXp * 100);
        progressText = isVirtualMax ? 'Virtual Max' : `${formatWithCommas(skill.currentXp)} / ${formatWithCommas(skill.nextLevelXp)}`;
    } else if (isMax) {
        levelDisplay = `${skill.level} (Max)`;
        progressPercent = 100;
        progressText = 'Mastered';
    } else {
        levelDisplay = skill.level;
        progressPercent = (skill.currentXp / skill.nextLevelXp * 100);
        progressText = `${formatWithCommas(skill.currentXp)} / ${formatWithCommas(skill.nextLevelXp)}`;
    }

    // Get tool info
    const tools = Object.values(PICKAXES);
    const highestOwned = gameState.getHighestPurchasedTool(SKILLS.MINING, tools);
    const activeTool = highestOwned || tools[0];
    const activeMultiplier = activeTool?.speedMultiplier || 1.0;
    const goldCount = gameState.getGold();

    // Find next upgrade
    const allToolsSorted = [...tools].sort((a, b) => a.level - b.level);
    const nextUpgrade = allToolsSorted.find(tool =>
        tool.price > 0 && !gameState.hasPurchasedTool(SKILLS.MINING, tool.id)
    );

    // Build upgrade HTML
    let upgradeHtml = '';
    if (nextUpgrade) {
        const isLocked = skill.level < nextUpgrade.level;
        const canAfford = goldCount >= nextUpgrade.price;

        if (isLocked) {
            upgradeHtml = `
                <span class="compact-upgrade locked">
                    → ${nextUpgrade.name} (${nextUpgrade.speedMultiplier}x)
                    <span class="upgrade-cost ${canAfford ? 'affordable' : 'cant-afford'}" data-price="${nextUpgrade.price}">${getItemIcon('coins')} ${formatNumber(nextUpgrade.price)}</span>
                    <span class="upgrade-lock">🔒 ${skill.level}/${nextUpgrade.level}</span>
                </span>
            `;
        } else {
            upgradeHtml = `
                <span class="compact-upgrade ${canAfford ? 'available' : ''}"
                      onclick="window.miningUI.upgradePickaxe('${nextUpgrade.id}')">
                    → ${nextUpgrade.name} (${nextUpgrade.speedMultiplier}x)
                    <span class="upgrade-cost ${canAfford ? 'affordable' : 'cant-afford'}" data-price="${nextUpgrade.price}">${getItemIcon('coins')} ${formatNumber(nextUpgrade.price)}</span>
                </span>
            `;
        }
    } else {
        upgradeHtml = `<span class="compact-upgrade maxed">✓ All pickaxes</span>`;
    }

    return `
        <div class="compact-header">
            <div class="compact-header-row">
                <div class="compact-title">
                    <span class="compact-icon">${getSkillIcon(SKILLS.MINING)}</span>
                    <span class="compact-name">Mining</span>
                    ${renderSkillInfo(SKILLS.MINING)}
                    <span class="compact-level">Lv ${levelDisplay}</span>
                    ${renderTalentButton(SKILLS.MINING)}
                </div>
                <div class="compact-stats">
                    <span class="compact-stat xp-total">
                        <span class="stat-label">Total XP:</span>
                        <span class="stat-value">${formatWithCommas(skill.xp)}</span>
                    </span>
                    <span class="compact-stat xp-rate">
                        <span class="stat-label">XP/h:</span>
                        <span class="stat-value">${formatWithCommas(xpPerHour)}</span>
                    </span>
                </div>
                <div class="compact-tool">
                    <span class="tool-label">Pickaxe:</span>
                    <span class="tool-current">${activeTool.name} (${activeMultiplier}x)</span>
                    ${upgradeHtml}
                </div>
            </div>
            <div class="compact-progress">
                <div class="compact-progress-bar">
                    <div class="compact-progress-fill" style="width: ${progressPercent}%"></div>
                    <span class="compact-progress-percent">${Math.floor(progressPercent)}%</span>
                </div>
                <span class="compact-progress-text">${progressText} XP</span>
            </div>
        </div>
    `;
}

/**
 * Get rock icon/sprite HTML
 */
function getRockIcon(rock) {
    if (rock.sprite) {
        const { sheet, x = 0, y = 0, width = 64, height = 64 } = rock.sprite;
        const isLarge = width >= 48 || height >= 48;
        const sizeClass = isLarge ? ' sprite-icon-lg' : '';
        const isSingleImage = x === 0 && y === 0;
        if (isSingleImage) {
            const atlas = getAtlasPercentBg(sheet);
            if (atlas) {
                return `<span class="sprite-icon ${sizeClass}" style="background-image: url('${atlas.atlas}'); background-size: ${atlas.bgSize}; background-position: ${atlas.bgPosition}; width: ${width}px; height: ${height}px;" data-hd-src="${sheet}"></span>`;
            }
        }
        const bgSize = isSingleImage ? 'background-size: contain;' : '';
        return `<span class="sprite-icon${sizeClass}" style="background-image: url('${sheet}'); background-position: -${x}px -${y}px; width: ${width}px; height: ${height}px; ${bgSize}"></span>`;
    }
    return rock.drops.length > 1 ? '💎' : '⛰️';
}

/**
 * Render a single rock card (Gatherer-style layout)
 */
function renderRockCard(rock, playerLevel, currentAction) {
    const isUnlocked = playerLevel >= rock.level;
    const isActive = currentAction?.type === 'mining' && currentAction?.rock?.id === rock.id;
    const progress = isActive ? currentAction.progress : 0;

    // Calculate effective mining time with all buffs
    // getEffectiveSpeedMultiplier includes: tool, talent tool, mastery, admin, talent speed, stacking
    const speedMultiplier = miningSkill.getEffectiveSpeedMultiplier();
    const corruptionSpeedMult = getCorruptionSpeedMultiplier(SKILLS.MINING);
    const corruptionXPMult = getCorruptionXPMultiplier(SKILLS.MINING);
    const totalSpeedMultiplier = speedMultiplier * corruptionSpeedMult;
    const effectiveTime = rock.baseTime / totalSpeedMultiplier;

    // Calculate effective XP with talent and corruption bonuses
    const talentXPBonus = getTalentXPBonus(SKILLS.MINING);
    let effectiveXP = rock.xp;
    if (corruptionXPMult > 1) effectiveXP = Math.floor(effectiveXP * corruptionXPMult);
    if (talentXPBonus > 0) effectiveXP = Math.floor(effectiveXP * (1 + talentXPBonus));

    // Get loot multipliers from corruption and talents (Rich Veins)
    const corruptionLootMult = getCorruptionLootMultiplier(SKILLS.MINING);
    const talentLootBonus = getTalentLootBonus(SKILLS.MINING);

    // Calculate total weight for percentages
    const totalWeight = rock.drops.reduce((sum, d) => sum + d.weight, 0);
    const isMultiDrop = rock.drops.length > 1;

    // Get rock icon (sprite or emoji fallback)
    const rockIcon = getRockIcon(rock);

    const cardClasses = [
        'rock-card',
        isUnlocked ? '' : 'locked',
        isActive ? 'active' : ''
    ].filter(Boolean).join(' ');

    // Build drops HTML with icons and quantities
    let dropsHtml = '';
    if (isMultiDrop) {
        // Gem vein - show percentages and quantity
        dropsHtml = rock.drops.map(drop => {
            const percent = (drop.weight / totalWeight) * 100;
            const itemName = getItemName(drop.itemId);
            const itemIcon = getItemIcon(drop.itemId);
            const baseQty = drop.quantity;
            const adjustedQty = talentLootBonus > 0 ? Math.max(1, Math.floor(baseQty * (1 + talentLootBonus))) : baseQty;
            const effectiveQty = adjustedQty * corruptionLootMult;
            const isBoosted = effectiveQty > baseQty;
            const qtyDisplay = isBoosted
                ? `<span class="drop-qty boosted">x${effectiveQty}</span>`
                : `<span class="drop-qty">x${baseQty}</span>`;
            return `
                <div class="drop-info gem-drop">
                    <span class="drop-name">${itemIcon} ${itemName}</span>
                    <span class="drop-stats">${qtyDisplay} <span class="drop-chance">${formatPercent(percent / 100)}</span></span>
                </div>
            `;
        }).join('');
    } else {
        // Single ore - show quantity
        const drop = rock.drops[0];
        const itemName = getItemName(drop.itemId);
        const itemIcon = getItemIcon(drop.itemId);
        const baseQty = drop.quantity;
        const adjustedQty = talentLootBonus > 0 ? Math.max(1, Math.floor(baseQty * (1 + talentLootBonus))) : baseQty;
        const effectiveQty = adjustedQty * corruptionLootMult;
        const isBoosted = effectiveQty > baseQty;
        const qtyDisplay = isBoosted
            ? `<span class="drop-qty boosted">x${effectiveQty}</span>`
            : `<span class="drop-qty">x${baseQty}</span>`;
        dropsHtml = `
            <div class="drop-info ore-drop">
                <span class="drop-name">${itemIcon} ${itemName}</span>
                ${qtyDisplay}
            </div>
        `;
    }

    return `
        <div class="${cardClasses}"
             data-rock-id="${rock.id}"
             style="--rock-color: ${rock.color}">
            <div class="rock-header">
                <span class="rock-level">${isUnlocked ? `Lvl ${rock.level}` : `Req: ${rock.level}`}</span>
                <div class="rock-icon-wrapper">${rockIcon}</div>
                <div class="rock-name">${rock.name}</div>
            </div>

            <div class="rock-info">
                <div class="rock-stat">
                    <span class="stat-icon mining-icon"></span>
                    <span class="stat-label">XP:</span>
                    <span class="stat-value xp ${corruptionXPMult > 1 ? 'boosted' : ''}">${effectiveXP}</span>
                </div>
                <div class="rock-stat">
                    <span class="stat-icon time-icon"></span>
                    <span class="stat-label">Time:</span>
                    <span class="stat-value ${corruptionSpeedMult > 1 ? 'boosted' : ''}">${(effectiveTime / 1000).toFixed(1)}s</span>
                </div>
            </div>

            <div class="rock-drops">
                ${dropsHtml}
            </div>

            <div class="rock-action">
                ${isUnlocked ? `
                    <button class="action-btn ${isActive ? 'active' : ''}"
                            onclick="window.miningUI.toggleMining('${rock.id}')">
                        ${isActive ? 'Stop' : 'Mine'}
                    </button>
                ` : `
                    <span class="locked-text">Locked</span>
                `}
            </div>

            <div class="rock-progress">
                <div class="rock-progress-fill" style="width: ${progress * 100}%"></div>
            </div>
        </div>
    `;
}

/**
 * Render the rock grid
 */
function renderRockGrid(playerLevel) {
    const currentAction = gameState.getCurrentAction();
    const rocks = Object.values(ROCKS);

    return `
        <div class="rock-grid">
            ${rocks.map(rock => renderRockCard(rock, playerLevel, currentAction)).join('')}
        </div>
    `;
}

/**
 * Render the mining interface
 */
export function renderMiningUI() {
    const container = document.getElementById('skill-content');
    const skill = gameState.getSkill(SKILLS.MINING);

    container.innerHTML = `
    <div class="mining-interface">
      ${renderCompactHeader()}

      <div class="mining-content">
        ${renderRockGrid(skill.level)}
      </div>
    </div>
  `;
}

/**
 * Toggle mining on/off for a rock
 */
export function toggleMining(rockId) {
    const currentAction = gameState.getCurrentAction();

    if (currentAction && currentAction.rock?.id === rockId) {
        miningSkill.stopMining();
    } else {
        // Check if we can mine first to get the reason if not
        const canResult = miningSkill.canMine(rockId);
        if (!canResult.success) {
            toastWarning(canResult.reason);
        } else {
            miningSkill.startMining(rockId);
        }
    }

    renderMiningUI();
}

/**
 * Upgrade pickaxe
 */
export function upgradePickaxe(pickaxeId) {
    const result = miningSkill.upgradePickaxe(pickaxeId);

    if (result.success) {
        renderMiningUI();
    } else {
        logger.warn('Tool upgrade failed:', result.reason);
    }
}

/**
 * Update mining UI each tick
 */
export function updateMiningUI() {
    // Update compact header stats
    const skill = gameState.getSkill(SKILLS.MINING);
    const xpPerHour = getXPPerHour(SKILLS.MINING);

    // Update XP total
    const xpTotalEl = document.querySelector('.compact-stat.xp-total .stat-value');
    if (xpTotalEl) xpTotalEl.textContent = formatWithCommas(skill.xp);

    // Update XP rate
    const xpRateEl = document.querySelector('.compact-stat.xp-rate .stat-value');
    if (xpRateEl) xpRateEl.textContent = formatWithCommas(xpPerHour);

    // Update tool upgrade gold color
    updateToolUpgradeCost('.mining-interface');

    // Update progress bar
    const isMax = skill.level >= GAME_CONFIG.MAX_LEVEL;
    const virtualEnabled = gameState.settings.virtualLevelsEnabled;
    const isVirtualMax = skill.virtualLevel >= GAME_CONFIG.VIRTUAL_LEVEL_CAP;

    let progressPercent, progressText;
    if (isMax && virtualEnabled) {
        progressPercent = isVirtualMax ? 100 : (skill.currentXp / skill.nextLevelXp * 100);
        progressText = isVirtualMax ? 'Virtual Max' : `${formatWithCommas(skill.currentXp)} / ${formatWithCommas(skill.nextLevelXp)}`;
    } else if (isMax) {
        progressPercent = 100;
        progressText = 'Mastered';
    } else {
        progressPercent = (skill.currentXp / skill.nextLevelXp * 100);
        progressText = `${formatWithCommas(skill.currentXp)} / ${formatWithCommas(skill.nextLevelXp)}`;
    }

    const progressFill = document.querySelector('.compact-progress-fill');
    if (progressFill) progressFill.style.width = `${progressPercent}%`;

    const progressPercentEl = document.querySelector('.compact-progress-percent');
    if (progressPercentEl) progressPercentEl.textContent = `${Math.floor(progressPercent)}%`;

    const progressTextEl = document.querySelector('.compact-progress-text');
    if (progressTextEl) progressTextEl.textContent = `${progressText} XP`;

    // Check if loot multiplier changed (corruption started/ended)
    const currentLootMultiplier = getCorruptionLootMultiplier(SKILLS.MINING);
    if (currentLootMultiplier !== lastLootMultiplier) {
        lastLootMultiplier = currentLootMultiplier;
        if (!isTalentTreeOpen()) {
            renderMiningUI();
        }
        return;
    }

    // Update action progress if mining is active
    const action = gameState.getCurrentAction();
    if (!action || action.type !== 'mining') return;

    const activeCard = document.querySelector(`.rock-card[data-rock-id="${action.rock.id}"]`);
    if (activeCard) {
        const progressFill = activeCard.querySelector('.rock-progress-fill');
        if (progressFill) {
            progressFill.style.width = `${action.progress * 100}%`;
        }
    }
}

/**
 * Setup mining skill listeners
 */
export function initMiningUI() {
    const originalCallback = miningSkill.onMiningComplete;
    miningSkill.onMiningComplete = (rock, xpResult) => {
        originalCallback.call(miningSkill, rock, xpResult);

        showNotification(`Mined ${rock.name}! +${rock.xp} XP`);

        if (xpResult.leveledUp) {
            showNotification(`Mining level ${xpResult.newLevel}!`, 'success');
        }

        // Only re-render if mining page is currently being viewed and talent tree is not open
        if (getCurrentSkillId() === SKILLS.MINING && !isTalentTreeOpen()) {
            const speedMult = IS_DEV ? adminPanel.state.global.speedMultiplier : 1;
            if (speedMult <= 1) {
                renderMiningUI();
            }
        }
    };

    // Listen for talent tree close to re-render mining UI
    window.addEventListener('talentTreeClosed', (e) => {
        if (e.detail.skillId === SKILLS.MINING && getCurrentSkillId() === SKILLS.MINING) {
            renderMiningUI();
        }
    });
}

function showNotification(message, type = 'info') {
    logger.game(message);
}

if (typeof window !== 'undefined') {
    window.miningUI = {
        toggleMining,
        upgradePickaxe
    };
}

export default {
    renderMiningUI,
    toggleMining,
    upgradePickaxe,
    updateMiningUI,
    initMiningUI
};
